/* ============================================================
   CustomerSlicingFiles — 61454143: finished print files on the
   customer order page. The server returns Completed jobs only
   for the order's customer; this block stays hidden until an
   artifact exists (no in-flight provider workspace leaked).
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { SlicingJob } from '../types/slicing';
import { plateLabel } from './slicingPlatesView';

function formatMinutesRu(min?: number): string {
  if (min == null || min <= 0) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
}

export function CustomerSlicingFiles({ orderId }: { orderId: string }) {
  const [files, setFiles] = useState<SlicingJob[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.listSlicingJobs(orderId)
      .then((jobs) => {
        if (!cancelled) setFiles(jobs.filter((j) => j.hasArtifact));
      })
      .catch(() => {
        if (!cancelled) setFiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (files == null || files.length === 0) return null;

  const download = async (job: SlicingJob) => {
    try {
      const blob = await api.downloadSlicingArtifact(job.jobId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.modelFileName.replace(/\.[^.]+$/, '')}.gcode.3mf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* download failures keep the list intact; a retry tap is enough */
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Файлы печати
      </p>
      {files.map((job) => {
        const eta = formatMinutesRu(job.result?.estimatedPrintMinutes);
        const grams = job.result ? Math.round(job.result.totalFilamentGrams ?? 0) : null;
        return (
          <div
            key={job.jobId}
            style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-caption)' }}
          >
            <span>{job.modelFileName}</span>
            {job.plateIndex != null && job.plateIndex > 0 && (
              <span style={{ color: 'var(--color-text-secondary)' }}>{plateLabel(job.plateIndex)}</span>
            )}
            {(eta || grams != null) && (
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {eta && `~${eta}`}{eta && grams != null ? ' · ' : ''}{grams != null ? `${grams} г` : ''}
              </span>
            )}
            <button className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} onClick={() => void download(job)}>
              ⬇ gcode.3mf
            </button>
          </div>
        );
      })}
    </div>
  );
}
